import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useAuth } from "./AuthContext";

const SocketContext = createContext({
  socket: null,
  connected: false,
  unreadChats: 0,
  chatNotif: null,
  newNotif: null,
  joinConversation: () => {},
  leaveConversation: () => {},
  sendMessage: () => {},
  onNewMessage: () => {},
  offNewMessage: () => {},
  clearUnreadChats: () => {},
});

const getSocketUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:5000";
  }
  return "https://api.glaexchange.online";
};

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();

  const socketRef = useRef(null);
  const [socket,      setSocket]      = useState(null);
  const [connected,   setConnected]   = useState(false);
  const [unreadChats, setUnreadChats] = useState(0);
  const [chatNotif,   setChatNotif]   = useState(null);
  // ✅ NEW: real-time notification from admin broadcast
  const [newNotif,    setNewNotif]    = useState(null);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const token = localStorage.getItem("gla_token");
    if (!token) return;

    import("socket.io-client")
      .then(({ io }) => {
        if (socketRef.current) socketRef.current.disconnect();

        const socketUrl = getSocketUrl();
        console.log("🔌 Connecting socket to:", socketUrl);

        const socketInstance = io(socketUrl, {
          auth: { token },
          transports: ["polling", "websocket"],
          withCredentials: true,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          timeout: 30000,
        });

        socketRef.current = socketInstance;
        setSocket(socketInstance);

        socketInstance.on("connect", () => {
          console.log("✅ Socket connected — transport:", socketInstance.io.engine.transport.name);
          setConnected(true);
        });

        socketInstance.on("disconnect", (reason) => {
          console.log("❌ Socket disconnected:", reason);
          setConnected(false);
        });

        socketInstance.on("connect_error", (err) => {
          console.warn("Socket connection error:", err.message);
          setConnected(false);
        });

        // ── Chat notification ────────────────────────────
        socketInstance.on("chat_notification", (data) => {
          setUnreadChats((prev) => prev + 1);
          setChatNotif(data);
          setTimeout(() => setChatNotif(null), 4000);

          import("react-hot-toast").then(({ default: toast }) => {
            toast(
              `💬 ${data.senderName}: ${data.text.slice(0, 50)}${data.text.length > 50 ? "…" : ""}`,
              {
                duration: 4000,
                position: "top-right",
                style: {
                  background: "#1a1a2e",
                  color: "#fff",
                  borderLeft: "4px solid #e53e3e",
                  fontSize: "0.85rem",
                },
              }
            );
          });
        });

        // ── Admin broadcast notification ─────────────────
        // Shows a toast AND bumps the bell icon in Navbar
        socketInstance.on("new_notification", (data) => {
          setNewNotif(data);   // Navbar watches this to refresh its list

          import("react-hot-toast").then(({ default: toast }) => {
            toast(
              `🔔 ${data.title}: ${data.message.slice(0, 60)}${data.message.length > 60 ? "…" : ""}`,
              {
                duration: 5000,
                position: "top-right",
                style: {
                  background: "var(--surface, #fff)",
                  color: "var(--text, #111)",
                  borderLeft: "4px solid #f59e0b",
                  fontSize: "0.85rem",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                },
              }
            );
          });
        });

        // Load initial unread chat count
        import("../utils/api")
          .then(({ default: api }) => {
            api.get("/chat/unread")
              .then(({ data }) => setUnreadChats(data.unreadCount))
              .catch(() => {});
          })
          .catch(() => {});
      })
      .catch((err) => {
        console.warn("Socket.io not available:", err.message);
      });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
  }, [user]);

  const joinConversation  = (id)   => socket?.emit("join_conversation", id);
  const leaveConversation = (id)   => socket?.emit("leave_conversation", id);
  const sendMessage       = (data) => socket?.emit("send_message", data);
  const onNewMessage      = (cb)   => socket?.on("new_message", cb);
  const offNewMessage     = (cb)   => socket?.off("new_message", cb);
  const clearUnreadChats  = ()     => setUnreadChats(0);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        unreadChats,
        chatNotif,
        newNotif,
        joinConversation,
        leaveConversation,
        sendMessage,
        onNewMessage,
        offNewMessage,
        clearUnreadChats,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    return {
      socket: null, connected: false,
      unreadChats: 0, chatNotif: null, newNotif: null,
      joinConversation: () => {}, leaveConversation: () => {},
      sendMessage: () => {}, onNewMessage: () => {}, offNewMessage: () => {},
      clearUnreadChats: () => {},
    };
  }
  return ctx;
};